import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEmployerJobs, updateJob } from '../services/jobService';
import { getCurrentUser } from '../services/authService';
import { getJobApplicationsOnce } from '../services/applicationService';
import { useWindowSize, BP } from '../hooks/useWindowSize';
import { getFunctions, httpsCallable } from 'firebase/functions';

const LANG_LABELS = { es: 'Español (Spanish)', zh: '中文简体 (Chinese Simplified)', 'zh-TW': '中文繁體 (Chinese Traditional)', ko: '한국어 (Korean)', ja: '日本語 (Japanese)' };

const STATUS_STYLES = {
  active:   { backgroundColor: '#d1fae5', color: '#065f46' },
  draft:    { backgroundColor: '#f3f4f6', color: '#374151' },
  paused:   { backgroundColor: '#fef3c7', color: '#92400e' },
  expired:  { backgroundColor: '#fee2e2', color: '#991b1b' },
  closed:   { backgroundColor: '#e5e7eb', color: '#6b7280' },
  canceled: { backgroundColor: '#f1f5f9', color: '#475569' },
};

const STATUS_LABELS = {
  active: 'Active', draft: 'Draft', paused: 'Paused',
  expired: 'Expired', closed: 'Closed', canceled: 'Canceled',
};

const STATUS_PRIORITY = { active: 0, draft: 1, paused: 2, closed: 3, expired: 4, canceled: 5 };

// HC-EMPLOYER-004a: only jobs in these statuses can still be canceled by the employer.
const CANCELABLE_STATUSES = new Set(['active', 'paused', 'draft']);

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { backgroundColor: '#e5e7eb', color: '#6b7280' };
  return (
    <span style={{ padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '12px', fontWeight: '500', backgroundColor: s.backgroundColor, color: s.color, whiteSpace: 'nowrap' }}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function getCompanyName(job) {
  return job.companyName ?? job.company ?? '';
}

function getLocation(job) {
  return [job.city, job.state].filter(Boolean).join(', ') || job.location || '';
}

function getSalary(job) {
  if (job.salaryMin != null || job.salaryMax != null) {
    if (job.salaryMin != null && job.salaryMax != null) return `$${job.salaryMin}–$${job.salaryMax}`;
    if (job.salaryMin != null) return `$${job.salaryMin}+`;
    return `Up to $${job.salaryMax}`;
  }
  return job.salary || '';
}

function getPostedLabel(job) {
  if (job.status === 'draft') return 'TBA';
  const ts = job.postedAt || job.createdAt;
  if (!ts) return '';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return 'Posted: ' + d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

function ManageJobsPage() {
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const isMobile = width < BP.mobile;
  const user = getCurrentUser();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applicantCounts, setApplicantCounts] = useState({});
  const [activeTab, setActiveTab] = useState('active');
  const [sortColumn, setSortColumn] = useState('postedDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [posterGenerating, setPosterGenerating] = useState({});
  const [posterDropdown, setPosterDropdown] = useState(null);
  const [posterDropdownPos, setPosterDropdownPos] = useState({ top: 0, left: 0 });
  const [posterLangMap, setPosterLangMap] = useState({});
  const [cancelTarget, setCancelTarget] = useState(null); // jobId pending cancel confirmation
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    const closeDropdown = () => setPosterDropdown(null);
    if (posterDropdown) {
      document.addEventListener('click', closeDropdown);
      return () => document.removeEventListener('click', closeDropdown);
    }
  }, [posterDropdown]);

  useEffect(() => {
    const loadJobs = async () => {
      try {
        const jobsData = await getEmployerJobs(user.uid);
        setJobs(jobsData);
        setLoading(false);
        
        // Load applicant counts after jobs are loaded
        const counts = {};
        for (const job of jobsData) {
          const applicants = await getJobApplicationsOnce(job.id, user.uid);
          counts[job.id] = applicants.length;
        }
        setApplicantCounts(counts);
      } catch (error) {
        console.error('Error loading jobs:', error);
        setLoading(false);
      }
    };

    if (user) {
      loadJobs();
    }
  }, [user]);

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    setCanceling(true);
    try {
      await updateJob(cancelTarget, { status: 'canceled' });
      setJobs(prev => prev.map(job => job.id === cancelTarget ? { ...job, status: 'canceled' } : job));
      setCancelTarget(null);
    } catch (error) {
      alert('Failed to cancel job');
    } finally {
      setCanceling(false);
    }
  };

  const handleGeneratePoster = async (jobId) => {
    const language = posterLangMap[jobId] || 'en';
    setPosterDropdown(null);
    setPosterGenerating(prev => ({ ...prev, [jobId]: true }));
    try {
      const fns = getFunctions();
      const generateJobPosterFn = httpsCallable(fns, 'generateJobPoster');
      const result = await generateJobPosterFn({ jobId, language });
      const { posterUrlLetter, posterUrlA4 } = result.data;
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, posterUrl: { letter: posterUrlLetter, a4: posterUrlA4 } } : j));
    } catch (err) {
      console.error('generateJobPoster error:', err);
      alert('Failed to generate poster. Please try again.');
    } finally {
      setPosterGenerating(prev => ({ ...prev, [jobId]: false }));
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const sortJobs = (jobList) => {
    return [...jobList].sort((a, b) => {
      if (sortColumn === 'applicants') {
        const aCount = applicantCounts[a.id] ?? 0;
        const bCount = applicantCounts[b.id] ?? 0;
        if (aCount !== bCount) return sortDirection === 'asc' ? aCount - bCount : bCount - aCount;
        // tiebreak by status priority so drafts never mix with active jobs
        const ap = STATUS_PRIORITY[a.status] ?? 99;
        const bp = STATUS_PRIORITY[b.status] ?? 99;
        return sortDirection === 'asc' ? ap - bp : bp - ap;
      }

      if (sortColumn === 'postedDate') {
        const tsA = a.postedAt || a.createdAt;
        const tsB = b.postedAt || b.createdAt;
        const msA = tsA ? (tsA.toDate ? tsA.toDate().getTime() : new Date(tsA).getTime()) : null;
        const msB = tsB ? (tsB.toDate ? tsB.toDate().getTime() : new Date(tsB).getTime()) : null;
        // drafts (no timestamp) always sort to the end regardless of direction
        if (msA === null && msB === null) return 0;
        if (msA === null) return 1;
        if (msB === null) return -1;
        return sortDirection === 'asc' ? msA - msB : msB - msA;
      }

      if (sortColumn === 'status') {
        const ap = STATUS_PRIORITY[a.status] ?? 99;
        const bp = STATUS_PRIORITY[b.status] ?? 99;
        return sortDirection === 'asc' ? ap - bp : bp - ap;
      }

      let aVal, bVal;
      if (sortColumn === 'title') {
        aVal = (a.title ?? '').toLowerCase();
        bVal = (b.title ?? '').toLowerCase();
      } else if (sortColumn === 'location') {
        aVal = getLocation(a).toLowerCase();
        bVal = getLocation(b).toLowerCase();
      } else if (sortColumn === 'salary') {
        aVal = getSalary(a).toLowerCase();
        bVal = getSalary(b).toLowerCase();
      } else if (sortColumn === 'type') {
        aVal = (a.type ?? '').toLowerCase();
        bVal = (b.type ?? '').toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const activeJobs = jobs.filter(j => j.status === 'active' || j.status === 'paused');
  const draftJobs = jobs.filter(j => j.status === 'draft');
  const hiredJobs = jobs.filter(j => j.status === 'closed');
  const historyJobs = jobs.filter(j => j.status === 'expired' || j.status === 'canceled');
  const displayedJobs = useMemo(() => {
    const list = activeTab === 'active' ? activeJobs
      : activeTab === 'drafts' ? draftJobs
      : activeTab === 'history' ? historyJobs
      : hiredJobs;
    return sortJobs(list);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, activeTab, applicantCounts, sortColumn, sortDirection]);

  /* Shared tab bar used in both mobile and desktop layouts */
  const tabBar = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 1rem', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap', gap: '4px' }}>
      <div style={{ display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', WebkitOverflowScrolling: 'touch', minWidth: 0 }}>
        {[
          { key: 'active',  label: `Active (${activeJobs.length})` },
          { key: 'drafts',  label: `Drafts (${draftJobs.length})` },
          { key: 'hired',   label: `Hired (${hiredJobs.length})` },
          { key: 'history', label: `History (${historyJobs.length})` },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: isMobile ? '0.75rem 1rem' : '1rem 1.5rem',
              border: 'none',
              borderBottom: activeTab === tab.key ? '3px solid #E8590C' : '3px solid transparent',
              backgroundColor: 'transparent',
              color: activeTab === tab.key ? '#E8590C' : '#6b7280',
              fontWeight: activeTab === tab.key ? '600' : '400',
              fontSize: isMobile ? '13px' : '14px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <button
        onClick={() => navigate('/post-job')}
        style={{ backgroundColor: '#E8590C', color: '#fff', padding: '7px 16px', borderRadius: '6px', border: 'none', fontSize: '13px', fontWeight: '500', cursor: 'pointer', margin: '6px 0' }}
      >
        + Post a Job
      </button>
    </div>
  );

  const emptyState = (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      {activeTab === 'active' ? (
        <>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>No active jobs. Post a new job to get started.</p>
          <button onClick={() => navigate('/post-job')} style={{ backgroundColor: '#E8590C', color: 'white', padding: '0.5rem 1rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '500' }}>
            Post Your First Job
          </button>
        </>
      ) : activeTab === 'drafts' ? (
        <p style={{ color: '#6b7280', marginBottom: 0 }}>No draft jobs.</p>
      ) : activeTab === 'history' ? (
        <p style={{ color: '#6b7280', marginBottom: 0 }}>No job history yet.</p>
      ) : (
        <p style={{ color: '#6b7280', marginBottom: 0 }}>No hired jobs yet.</p>
      )}
    </div>
  );

  return (
    <>
      {/* Best viewed on desktop notice — tablet only */}
      {!isMobile && width < BP.tablet && (
        <div style={{
          backgroundColor: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontSize: '14px',
          color: '#92400e',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          This page is best viewed on a desktop for full functionality.
        </div>
      )}

      {isMobile ? (
        /* ── MOBILE: card layout ── */
        <div style={{ overflowX: 'hidden', maxWidth: '100vw' }}>
          <div style={{ backgroundColor: '#F8FAFC', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '12px' }}>
            {tabBar}
          </div>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading jobs...</div>
          ) : displayedJobs.length === 0 ? (
            <div style={{ backgroundColor: '#F8FAFC', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>{emptyState}</div>
          ) : (
            displayedJobs.map((job) => (
              <div key={job.id} style={{ backgroundColor: '#F8FAFC', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
                {/* Title + status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', gap: '8px' }}>
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#0F1E2E', flex: 1 }}>{job.title}</div>
                  <StatusBadge status={job.status} />
                </div>
                {/* Company · location */}
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                  {[getCompanyName(job), getLocation(job)].filter(Boolean).join(' · ')}
                </div>
                {/* Salary · type */}
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                  {[getSalary(job), job.type].filter(Boolean).join(' · ')}
                </div>
                {/* Posted date */}
                {getPostedLabel(job) && (
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '12px' }}>
                    {getPostedLabel(job)}
                  </div>
                )}
                {/* Applicant count */}
                <div style={{ fontSize: '13px', color: '#374151' }}>
                  {applicantCounts[job.id] ?? 0} applicant{(applicantCounts[job.id] ?? 0) !== 1 ? 's' : ''}
                </div>
                {/* Action buttons */}
                <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', flexWrap: 'nowrap', width: '100%', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #1a3048' }}>
                  <button
                    onClick={() => navigate(`/jobs/${job.id}/applicants`)}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', padding: '8px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: '#0F1E2E', color: 'white', flex: 1 }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    View Applicants
                  </button>
                  {job.status !== 'closed' && job.status !== 'canceled' && (
                    <button
                      onClick={() => navigate(`/edit-job/${job.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', padding: '8px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: '#1a3048', color: 'white', flexShrink: 0 }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                      Edit
                    </button>
                  )}
                  {/* Poster action — mobile */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); if (posterDropdown === job.id) { setPosterDropdown(null); } else { const rect = e.currentTarget.getBoundingClientRect(); setPosterDropdownPos({ top: rect.bottom + 4, left: Math.max(4, rect.right - 180) }); setPosterDropdown(job.id); } }}
                      disabled={posterGenerating[job.id]}
                      title="Job Poster"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', borderRadius: '6px', border: '1px solid', borderColor: job.posterUrl ? '#E8590C' : '#d1d5db', cursor: posterGenerating[job.id] ? 'not-allowed' : 'pointer', backgroundColor: job.posterUrl ? 'rgba(232,89,12,0.08)' : 'transparent', color: job.posterUrl ? '#E8590C' : '#6b7280', flexShrink: 0 }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                        <rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>
                      </svg>
                    </button>
                    {posterDropdown === job.id && !posterGenerating[job.id] && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ position: 'fixed', top: posterDropdownPos.top, left: posterDropdownPos.left, zIndex: 1000, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: '200px', padding: '4px 0' }}
                      >
                        {job.secondLanguage && (
                          <div style={{ padding: '8px 16px 4px' }}>
                            <select
                              value={posterLangMap[job.id] || 'en'}
                              onChange={(e) => setPosterLangMap(prev => ({ ...prev, [job.id]: e.target.value }))}
                              style={{ width: '100%', padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '12px', color: '#374151', backgroundColor: 'white' }}
                            >
                              <option value="en">English</option>
                              <option value={job.secondLanguage}>{LANG_LABELS[job.secondLanguage]}</option>
                            </select>
                          </div>
                        )}
                        <div style={{ borderTop: '1px solid #e5e7eb', margin: '4px 0' }} />
                        {job.posterUrl ? (
                          <>
                            <a href={job.posterUrl.letter + '?t=' + Date.now()} target="_blank" rel="noreferrer" onClick={() => setPosterDropdown(null)}
                              style={{ display: 'block', padding: '8px 16px', fontSize: '13px', color: '#374151', textDecoration: 'none' }}>
                              Download (Letter)
                            </a>
                            <a href={job.posterUrl.a4 + '?t=' + Date.now()} target="_blank" rel="noreferrer" onClick={() => setPosterDropdown(null)}
                              style={{ display: 'block', padding: '8px 16px', fontSize: '13px', color: '#374151', textDecoration: 'none' }}>
                              Download (A4)
                            </a>
                            <div style={{ borderTop: '1px solid #e5e7eb', margin: '4px 0' }} />
                            <button onClick={() => handleGeneratePoster(job.id)}
                              style={{ display: 'block', width: '100%', padding: '8px 16px', fontSize: '13px', color: '#374151', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}>
                              Regenerate Poster
                            </button>
                          </>
                        ) : (
                          <button onClick={() => handleGeneratePoster(job.id)}
                            style={{ display: 'block', width: '100%', padding: '8px 16px', fontSize: '13px', color: '#374151', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}>
                            Generate Poster
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {CANCELABLE_STATUSES.has(job.status) && (
                    <button
                      onClick={() => setCancelTarget(job.id)}
                      title="Cancel Job"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', borderRadius: '6px', border: '1px solid #dc2626', cursor: 'pointer', backgroundColor: 'transparent', color: '#dc2626', flexShrink: 0 }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        /* ── TABLET / DESKTOP: CSS Grid table ── */
        <div style={{ overflowX: 'auto', width: '100%' }}>
        <div style={{ minWidth: '780px' }}>
        <div style={{ backgroundColor: '#F8FAFC', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          {tabBar}
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading jobs...</div>
          ) : displayedJobs.length === 0 ? (
            emptyState
          ) : (
            <div style={{ width: '100%' }}>
              {/* Header row */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1fr 1fr 0.8fr', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', padding: '0.75rem 1rem', gap: '0.5rem' }}>
                {[['title', 'Job'], ['salary', 'Details'], ['postedDate', 'Posted Date'], ['applicants', 'Applicants / Status']].map(([col, label]) => (
                  <div key={col} onClick={() => handleSort(col)} style={{ fontWeight: '600', fontSize: '14px', color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {label}
                      {sortColumn === col && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          {sortDirection === 'asc' ? <path d="M12 19V5M5 12l7-7 7 7"/> : <path d="M12 5v14M5 12l7 7 7-7"/>}
                        </svg>
                      )}
                    </span>
                  </div>
                ))}
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151', textAlign: 'center' }}>Actions</div>
              </div>

              {/* Data rows */}
              {displayedJobs.map((job) => (
                <div key={job.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1fr 1fr 0.8fr', borderBottom: '1px solid #e5e7eb', padding: '1rem', gap: '0.5rem', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#0F1E2E' }}>{job.title}</div>
                    <div style={{ fontSize: '12px', color: '#4b5563' }}>{getCompanyName(job)}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>{getLocation(job)}</div>
                    {job.status === 'closed' && job.closedAt && (
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                        Closed on {job.closedAt.toDate
                          ? job.closedAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : new Date(job.closedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', color: '#374151' }}>{job.type}</div>
                    <div style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>{getSalary(job)}</div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                    {getPostedLabel(job)}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <StatusBadge status={job.status} />
                    <div style={{ fontSize: '13px', color: '#374151' }}>
                      {applicantCounts[job.id] ?? 0} applicant{(applicantCounts[job.id] ?? 0) !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                    <div onClick={() => navigate(`/jobs/${job.id}/applicants`)} title="View Applicants" style={{ position: 'relative', cursor: 'pointer', color: '#8b5cf6', display: 'flex', alignItems: 'center' }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                      {applicantCounts[job.id] > 0 && (
                        <span style={{ position: 'absolute', top: '-8px', right: '-8px', backgroundColor: '#10b981', color: 'white', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold' }}>
                          {applicantCounts[job.id] > 99 ? '99+' : applicantCounts[job.id]}
                        </span>
                      )}
                    </div>
                    {/* Poster action */}
                    <div style={{ position: 'relative' }}>
                      <div
                        onClick={(e) => { e.stopPropagation(); if (posterDropdown === job.id) { setPosterDropdown(null); } else { const rect = e.currentTarget.getBoundingClientRect(); setPosterDropdownPos({ top: rect.bottom + 4, left: Math.max(4, rect.right - 180) }); setPosterDropdown(job.id); } }}
                        title={posterGenerating[job.id] ? 'Generating poster…' : 'Job Poster'}
                        style={{ cursor: posterGenerating[job.id] ? 'not-allowed' : 'pointer', color: job.posterUrl ? '#E8590C' : '#4b5563', display: 'flex', alignItems: 'center', opacity: posterGenerating[job.id] ? 0.5 : 1 }}
                      >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                          <rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>
                        </svg>
                      </div>
                      {posterDropdown === job.id && !posterGenerating[job.id] && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{ position: 'fixed', top: posterDropdownPos.top, left: posterDropdownPos.left, zIndex: 1000, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: '200px', padding: '4px 0' }}
                        >
                          {job.secondLanguage && (
                            <div style={{ padding: '8px 16px 4px' }}>
                              <select
                                value={posterLangMap[job.id] || 'en'}
                                onChange={(e) => setPosterLangMap(prev => ({ ...prev, [job.id]: e.target.value }))}
                                style={{ width: '100%', padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '12px', color: '#374151', backgroundColor: 'white' }}
                              >
                                <option value="en">English</option>
                                <option value={job.secondLanguage}>{LANG_LABELS[job.secondLanguage]}</option>
                              </select>
                            </div>
                          )}
                          <div style={{ borderTop: '1px solid #e5e7eb', margin: '4px 0' }} />
                          {job.posterUrl ? (
                            <>
                              <a href={job.posterUrl.letter + '?t=' + Date.now()} target="_blank" rel="noreferrer" onClick={() => setPosterDropdown(null)}
                                style={{ display: 'block', padding: '8px 16px', fontSize: '13px', color: '#374151', textDecoration: 'none' }}>
                                Download Letter (US)
                              </a>
                              <a href={job.posterUrl.a4 + '?t=' + Date.now()} target="_blank" rel="noreferrer" onClick={() => setPosterDropdown(null)}
                                style={{ display: 'block', padding: '8px 16px', fontSize: '13px', color: '#374151', textDecoration: 'none' }}>
                                Download A4
                              </a>
                              <div style={{ borderTop: '1px solid #e5e7eb', margin: '4px 0' }} />
                              <button onClick={() => handleGeneratePoster(job.id)}
                                style={{ display: 'block', width: '100%', padding: '8px 16px', fontSize: '13px', color: '#374151', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}>
                                Regenerate Poster
                              </button>
                            </>
                          ) : (
                            <button onClick={() => handleGeneratePoster(job.id)}
                              style={{ display: 'block', width: '100%', padding: '8px 16px', fontSize: '13px', color: '#374151', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}>
                              Generate Poster
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {job.status !== 'closed' && job.status !== 'canceled' && (
                      <div onClick={() => navigate(`/edit-job/${job.id}`)} title="Edit Job" style={{ cursor: 'pointer', color: '#0369a1' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </div>
                    )}
                    {CANCELABLE_STATUSES.has(job.status) && (
                      <div onClick={() => setCancelTarget(job.id)} title="Cancel Job" style={{ cursor: 'pointer', color: '#dc2626' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="15" y1="9" x2="9" y2="15"/>
                          <line x1="9" y1="9" x2="15" y2="15"/>
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
        </div>
      )}

      {cancelTarget && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white',
            border: '2px solid #dc2626',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '480px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#991b1b', marginBottom: '12px' }}>
              Cancel This Job?
            </div>
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '20px', lineHeight: '1.5' }}>
              Are you sure you want to cancel this job? All in-pipeline applicants will be automatically rejected and notified.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={handleConfirmCancel}
                disabled={canceling}
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: canceling ? 'not-allowed' : 'pointer',
                  opacity: canceling ? 0.7 : 1,
                }}
              >
                {canceling ? 'Canceling…' : 'Cancel Job'}
              </button>
              <button
                onClick={() => setCancelTarget(null)}
                disabled={canceling}
                style={{
                  backgroundColor: 'white',
                  color: '#374151',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: canceling ? 'not-allowed' : 'pointer',
                }}
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ManageJobsPage;